import React, { createElement } from 'react'

import { AnalyticsEvent } from 'src/types'
import { APP_NAME } from 'src/constants'

import { NavUser } from 'src/features/NavUser'

import { useAppSidebar } from 'src/features/AppSidebar/useAppSidebar'
import { AppSidebarProps } from 'src/features/AppSidebar/types'
import { ICON_SIZE } from 'src/features/AppSidebar/constants'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
} from 'src/components/shadcn/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/shadcn/collapsible'

const AppSidebar = ({ setIsOpenSideBar }: AppSidebarProps): JSX.Element => {
  const {
    listOfRoutes,
    getIsRouteActive,
    goToHome,
    settings,
    logEvent,
    isMobile,
  } = useAppSidebar()

  return (
    <Sidebar collapsible="icon">
      <SidebarContent className={isMobile ? 'bg-background' : ''}>
        <SidebarGroup>
          <SidebarGroupLabel
            className="text-lg justify-center cursor-pointer font-bold my-3"
            onClick={() => {
              logEvent('sidebar_click', { item: 'home' })
              goToHome()
            }}
          >
            {APP_NAME}
          </SidebarGroupLabel>
          <SidebarMenu>
            {listOfRoutes.map(
              ({ title, onClick, isActive, icon, children }) => {
                const handleClick = () => {
                  logEvent(AnalyticsEvent.SIDEBAR_CLICK, { item: title })
                  onClick?.()
                }

                return children.length > 0 ? (
                  <Collapsible
                    key={title}
                    defaultOpen={getIsRouteActive(children)}
                    className="group/collapsible cursor-pointer select-none"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          onClick={() => {
                            setIsOpenSideBar(true)
                            logEvent('sidebar_click', { item: title })
                          }}
                          className="rounded-sm"
                          asChild
                        >
                          <a>
                            {icon &&
                              createElement(icon, {
                                size: ICON_SIZE,
                                strokeWidth: 2,
                              })}
                            <span className="text-md font-normal">{title}</span>
                          </a>
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {children.map(
                          ({
                            title: childTitle,
                            onClick: childOnClick,
                            isActive: childIsActive,
                          }) => {
                            const handleChildClick = () => {
                              logEvent('sidebar_click', { item: childTitle })
                              childOnClick()
                            }

                            return (
                              <SidebarMenuSub key={childTitle}>
                                <SidebarMenuSubItem className="cursor-pointer select-none">
                                  <SidebarMenuButton
                                    className="rounded-sm"
                                    isActive={childIsActive}
                                    onClick={handleChildClick}
                                    asChild
                                  >
                                    <span className="text-md font-normal">
                                      {childTitle}
                                    </span>
                                  </SidebarMenuButton>
                                </SidebarMenuSubItem>
                              </SidebarMenuSub>
                            )
                          },
                        )}
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ) : (
                  <SidebarMenuItem
                    className="cursor-pointer select-none"
                    key={title}
                  >
                    <SidebarMenuButton
                      className="rounded-sm"
                      isActive={isActive}
                      onClick={handleClick}
                      asChild
                    >
                      <a>
                        {icon &&
                          createElement(icon, {
                            size: ICON_SIZE,
                            strokeWidth: 2,
                          })}
                        <span className="text-md font-normal">{title}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              },
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className={isMobile ? 'bg-background' : ''}>
        <SidebarMenuButton
          className="rounded-sm cursor-pointer select-none"
          isActive={settings.isActive}
          onClick={() => {
            logEvent('sidebar_click', { item: settings.title })
            settings.onClick()
          }}
          asChild
        >
          <a>
            {settings.icon &&
              createElement(settings.icon, {
                size: ICON_SIZE,
                strokeWidth: 2,
              })}
            <span className="text-md font-normal">{settings.title}</span>
          </a>
        </SidebarMenuButton>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}

export default AppSidebar
